import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Info, Radio, CalendarClock, Sparkles } from "lucide-react";
import { BrandLogo } from "@/components/shell/Brand";
import { useSession } from "@/context/SessionContext";
import { useI18n } from "@/i18n";
import { register as registerAccount } from "@/api/auth";

function AuthShell({ mode }: { mode: "login" | "register" }) {
  const { d, lang } = useI18n();
  const { login } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [notice, setNotice] = useState<string | null>(null);
  const [createdNoLogin, setCreatedNoLogin] = useState(false);
  const [busy, setBusy] = useState(false);

  const isLogin = mode === "login";
  const tr = lang === "tr";

  async function submitRegister() {
    if (password.length < 12) {
      setNotice(tr ? "Şifre en az 12 karakter olmalı." : "Password must be at least 12 characters.");
      return;
    }
    setBusy(true);
    const reg = await registerAccount(email.trim(), password, fullName);
    if (!reg.ok) {
      setBusy(false);
      const r = (reg as { reason?: string }).reason;
      setNotice(r === "exists"
        ? (tr ? "Bu e-posta adresiyle zaten bir hesap bulunuyor." : "An account with this email already exists.")
        : r === "invalid"
        ? (tr ? "Bilgiler kabul edilmedi. Geçerli bir e-posta ve en az 12 karakterli bir şifre girin." : "Details were not accepted. Enter a valid email and a password of at least 12 characters.")
        : r === "rate_limited"
        ? (tr ? "Çok fazla kayıt denemesi yapıldı. Lütfen daha sonra tekrar deneyin." : "Too many registration attempts. Please try again later.")
        : (tr ? "Kayıt şu anda tamamlanamadı. Lütfen tekrar deneyin." : "Registration could not be completed right now. Please try again."));
      return;
    }
    const res = await login(email.trim(), password);
    setBusy(false);
    if (res.ok) {
      navigate("/app", { replace: true });
    } else {
      setCreatedNoLogin(true);
      setNotice(tr ? "Hesabın oluşturuldu. Giriş yapmak için devam et." : "Your account was created. Continue to sign in.");
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setNotice(null);
    if (!isLogin) {
      await submitRegister();
      return;
    }
    setBusy(true);
    const res = await login(email, password);
    setBusy(false);
    if (res.ok) {
      navigate(from && from.startsWith("/app") ? from : "/app", { replace: true });
    } else {
      // Friendly, user-facing messages only — never expose backend/internal terms.
      const reason = (res as { reason?: string }).reason;
      const msg = reason === "invalid"
        ? (lang === "tr" ? "E-posta veya şifre hatalı. Lütfen tekrar dene." : "Incorrect email or password. Please try again.")
        : reason === "network"
        ? (lang === "tr" ? "Bağlantı sorunu oluştu. Lütfen tekrar dene." : "A connection problem occurred. Please try again.")
        : (lang === "tr" ? "Giriş şu anda tamamlanamadı. Lütfen daha sonra tekrar dene." : "Sign-in could not be completed right now. Please try again later.");
      setNotice(msg);
    }
  }

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      {/* form */}
      <div className="flex flex-col justify-center px-6 py-12 sm:px-12 lg:px-16">
        <div className="mx-auto w-full max-w-sm">
          <BrandLogo />
          <h1 className="mt-10 text-3xl font-bold tracking-tight text-foreground">
            {isLogin ? (lang === "tr" ? "Tekrar hoş geldin" : "Welcome back") : (lang === "tr" ? "SDIP AI'ye katıl" : "Join SDIP AI")}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {isLogin ? (lang === "tr" ? "Zekâ merkezine giriş yap." : "Sign in to your intelligence cockpit.") : (lang === "tr" ? "Premium spor zekâsına erişim oluştur." : "Create access to premium sports intelligence.")}
          </p>

          {isLogin && (
            <div className="mt-6 rounded-2xl border border-atlas/25 bg-atlas/[0.06] p-4" data-testid="onboarding-trial">
              <p className="text-sm font-bold text-foreground">{lang === "tr" ? "SDIP AI'ı 3 gün ücretsiz deneyin" : "Try SDIP AI free for 3 days"}</p>
              <p className="mt-1 text-xs text-muted-foreground">
                {lang === "tr" ? "Canlı sinyaller, maç öncesi analizler ve ATLAS Asistan ile 72 saat ELITE erişim. Kart gerekmez." : "72 hours of ELITE access with live signals, prematch analysis and the ATLAS Assistant. No card required."}
              </p>
              <p className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-atlas-300">
                <Sparkles className="h-3.5 w-3.5" /> {lang === "tr" ? "Giriş yaptıktan sonra: 3 Gün Ücretsiz Başla" : "After signing in: Start your 3-Day Free Trial"}
              </p>
            </div>
          )}

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid={`${mode}-form`}>
            {!isLogin && (
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-muted-foreground">{tr ? "Ad Soyad (opsiyonel)" : "Full name (optional)"}</label>
                <input type="text" autoComplete="name" value={fullName} onChange={(e) => setFullName(e.target.value)} data-testid="auth-full-name"
                  className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-foreground outline-none transition focus:border-atlas/40" placeholder={tr ? "Adın Soyadın" : "Your name"} />
              </div>
            )}
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-muted-foreground">Email</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="auth-email"
                className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-foreground outline-none transition focus:border-atlas/40" placeholder="you@sdip.ai" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-muted-foreground">{lang === "tr" ? "Şifre" : "Password"}</label>
              <input type="password" required autoComplete={isLogin ? "current-password" : "new-password"} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="auth-password"
                className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-foreground outline-none transition focus:border-atlas/40" placeholder="••••••••" />
              {!isLogin && <p className="mt-1.5 text-[11px] text-muted-foreground" data-testid="auth-password-hint">{tr ? "En az 12 karakter" : "At least 12 characters"}</p>}
            </div>
            <button type="submit" disabled={busy} data-testid="auth-submit"
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-atlas-500 to-atlas-600 px-4 py-3 text-sm font-bold text-atlas-ink transition hover:shadow-glow disabled:opacity-50">
              {busy && !isLogin ? (tr ? "Hesap oluşturuluyor…" : "Creating account…") : (isLogin ? d.nav.login : d.nav.register)} <ArrowRight className="h-4 w-4" />
            </button>
          </form>

          {notice && (
            <div className="mt-4 flex items-start gap-2 rounded-xl border border-gold/25 bg-gold/10 px-4 py-3 text-xs text-gold-300" data-testid="auth-notice">
              <Info className="mt-0.5 h-4 w-4 shrink-0" /> {notice}
            </div>
          )}
          {createdNoLogin && (
            <Link to="/login" data-testid="register-continue-login" className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border border-atlas/30 px-4 py-3 text-sm font-semibold text-atlas-300 transition hover:bg-atlas/10">
              {d.nav.login} <ArrowRight className="h-4 w-4" />
            </Link>
          )}

          <p className="mt-6 text-center text-sm text-muted-foreground">
            {isLogin ? (lang === "tr" ? "Hesabın yok mu? " : "No account? ") : (lang === "tr" ? "Zaten üye misin? " : "Already a member? ")}
            <Link to={isLogin ? "/register" : "/login"} className="font-semibold text-atlas-300">{isLogin ? d.nav.register : d.nav.login}</Link>
          </p>
        </div>
      </div>

      {/* visual */}
      <div className="relative hidden overflow-hidden border-l border-line bg-navy-900 lg:block">
        <div className="pointer-events-none absolute inset-0 grid-noise opacity-30" />
        <div className="pointer-events-none absolute -right-20 top-20 h-96 w-96 rounded-full bg-atlas/15 blur-[120px]" />
        <div className="relative flex h-full flex-col justify-center px-16">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <p className="eyebrow text-atlas-300">ATLAS Intelligence Engine</p>
            <h2 className="mt-3 max-w-md text-4xl font-bold leading-tight tracking-tight text-foreground">
              {lang === "tr" ? "Sahadaki her sinyali okuyan premium spor zekâsı." : "Premium sports intelligence that reads every signal on the pitch."}
            </h2>
            <div className="mt-10 space-y-4">
              {[[Radio, lang === "tr" ? "Canlı market zekâsı" : "Live market intelligence"], [CalendarClock, lang === "tr" ? "Kanıta dayalı maç öncesi" : "Evidence-driven prematch"], [Sparkles, lang === "tr" ? "ATLAS asistanı" : "ATLAS assistant"]].map(([Icon, label]: any) => (
                <div key={label} className="flex items-center gap-3 text-sm text-foreground/90">
                  <span className="grid h-9 w-9 place-items-center rounded-lg bg-atlas/10 text-atlas-400"><Icon className="h-4 w-4" /></span>{label}
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

export function Login() { return <AuthShell mode="login" />; }
export function Register() { return <AuthShell mode="register" />; }
